/*
 * If not stated otherwise in this file or this component's LICENSE file the
 * following copyright and licenses apply:
 *
 * Copyright 2020 Metrological
 *
 * Licensed under the Apache License, Version 2.0 (the License);
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AppData, Router, Utils } from '@lightningjs/sdk';
import NavBar from 'components/widgets/NavBar';
import LoadingSpinner from 'components/common/LoadingSpinner';
import routerConfig from 'config/routes';
import { isRouteDisabled } from 'support/routerUtils';
import AppLaunch from 'components/widgets/AppLaunch';
import ErrorModal from 'components/widgets/ErrorModal';
import AdvancedErrorModal from 'components/widgets/AdvancedErrorModal';
import Announcer from 'config/Announcer';
import EnterParentalPin from 'components/widgets/parental-pin/EnterParentalPin';
import ResetParentalPin from 'components/widgets/parental-pin/ResetParentalPin';
import SetParentalPin from 'components/widgets/parental-pin/SetParentalPin';
import ExitModal from 'components/widgets/ExitModal';

/*
Do not remove, we load the VideoPlayer to create a video element that we will potentially use later
see node_modules/@metrological/sdk/src/VideoPlayer/index.js
*/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { VideoPlayer } from '@lightningjs/sdk';
import TopNav from 'components/widgets/TopNav';
import AyswModal from 'components/widgets/AyswModal';
import LoginLanding from 'components/pages/login/login-landing/LoginLanding';
import EmailInputModal from 'components/pages/login/email-input/EmailInputModal';
import SignUpModal from 'components/pages/login/SignUpModal';
import SignInModal from 'components/pages/login/sign-in/SignInModal';
import CreatePasswordModal from 'components/pages/login/create-password/CreatePasswordModal';
import SignInConfirmationModal from 'components/pages/login/confirmation/SignInConfirmationModal';
import SignInPasswordModal from 'components/pages/login/sign-in-password/SignInPasswordModal';
import ForgotPasswordModal from 'components/pages/login/forgot-password/ForgotPasswordModal';
import EmailConfirmationModal from 'components/pages/login/confirmation/EmailConfirmationModal';
import ForgotPasswordConfirmationModal from 'components/pages/login/confirmation/ForgotPasswordConfirmationModal';
import SignOutModal from 'components/pages/login/sign-out/SignOutModal';
import RemoveConfirmationModal from 'components/pages/myStuff/RemoveConfirmationModal';
import BrazeNotification from 'components/widgets/notification/BrazeNotification';
import { isKeySimulated } from 'support/keyEventUtils';
import { closeApp } from 'support/appUtils';

// Keys we should not allow multiple key presses by holding
const KEYS_PREVENT_HOLDING = ['Enter', 'Back', 'Backspace'];
export default class App extends Announcer {
  // Assumes typically one key can be held/pressed at once
  private _isAnyKeyHeld = false;

  static language() {
    return 'en';
  }

  static colors() {
    return './static/colors.json';
  }

  static getFonts() {
    return [
      {
        family: 'Roboto',
        url: Utils.asset('fonts/Roboto-Regular.ttf'),
        descriptors: {
          weight: 'normal',
        },
      },
      {
        family: 'Roboto',
        url: Utils.asset('fonts/Roboto-Bold.ttf'),
        descriptors: { weight: 'bold' },
      },
      { family: 'Roboto-Light', url: Utils.asset('fonts/Roboto-Light.ttf') },
      { family: 'Roboto-Medium', url: Utils.asset('fonts/Roboto-Medium.ttf') },
      { family: 'Roboto-Bold', url: Utils.asset('fonts/Roboto-Bold.ttf') },
      { family: 'Roboto-Black', url: Utils.asset('fonts/Roboto-Black.ttf') },
    ];
  }

  static override _template() {
    return {
      ...super._template(),
      Loading: {
        type: LoadingSpinner,
        visible: false,
        x: 960,
        y: 540,
        isLoadingScreen: true,
      },
      Widgets: {
        AdvancedErrorModal: {
          type: AdvancedErrorModal,
        },
        AppLaunch: {
          type: AppLaunch,
        },
        AyswModal: {
          type: AyswModal,
        },
        BrazeNotification: {
          type: BrazeNotification,
        },
        CreatePasswordModal: {
          type: CreatePasswordModal,
        },
        EmailInputModal: {
          type: EmailInputModal,
        },
        EmailConfirmationModal: {
          type: EmailConfirmationModal,
        },
        ForgotPasswordConfirmationModal: {
          type: ForgotPasswordConfirmationModal,
        },
        EnterParentalPin: {
          type: EnterParentalPin,
        },
        ErrorModal: {
          type: ErrorModal,
        },
        ExitModal: {
          type: ExitModal,
        },
        ForgotPasswordModal: {
          type: ForgotPasswordModal,
        },
        LoginLanding: {
          type: LoginLanding,
        },
        NavBar: {
          type: NavBar,
        },
        ResetParentalPin: {
          type: ResetParentalPin,
        },
        RemoveConfirmationModal: {
          type: RemoveConfirmationModal,
        },
        SetParentalPin: {
          type: SetParentalPin,
        },
        SignUpModal: {
          type: SignUpModal,
        },
        SignInModal: {
          type: SignInModal,
        },
        SignInConfirmationModal: {
          type: SignInConfirmationModal,
        },
        SignInPasswordModal: {
          type: SignInPasswordModal,
        },
        SignOutModal: {
          type: SignOutModal,
        },
        TopNav: {
          type: TopNav,
        },
      },
    };
  }

  override _setup() {
    super._setup();

    // Here we create a video element, store it in the VideoPlayer's state, then remove it from the document
    VideoPlayer.playing; // Do not remove, this is called to trigger the players autoSetupMixin function
    const videoElement = document.getElementsByTagName('video')[0];
    if (videoElement) {
      videoElement.remove();
    }

    const routes = routerConfig.routes.filter(
      route => !isRouteDisabled(route.path),
    );
    Router.startRouter({ ...routerConfig, routes }, this);
    this.initializeAnnouncer();

    this.application.on('loadingScreen', isLoading => {
      if (isLoading) {
        this.announcerHandleLoadingStart();
      } else {
        this.announcerHandleLoadingEnd();
      }
    });
  }

  override _active() {
    AppData?.device.onAppActive();
  }

  override _captureKey(event: KeyboardEvent) {
    // If a OneTrust display is present, allow OneTrust to handle the key events
    if (AppData?.oneTrustService?.isDialogOpen) return true;

    if (isKeySimulated(event)) return false;

    AppData?.device.handleDebugKeyInputs(event, this);

    const key = event.key;
    if (KEYS_PREVENT_HOLDING.includes(key) && this._isAnyKeyHeld) {
      AppData?.log.debug('Suppressing Rapid Key Inputs', 'key:', key);
      return true;
    }

    this._isAnyKeyHeld = true;

    return Router.isNavigating();
  }

  override _captureKeyRelease(event: KeyboardEvent) {
    this._isAnyKeyHeld = false;

    if (AppData?.oneTrustService?.isDialogOpen) return true;

    return false;
  }

  override _captureExit(event: KeyboardEvent) {
    if (!AppData?.device.shouldCloseOnExit(event)) return false;

    closeApp();
    return true;
  }

  // Let captureExit handle app exits
  override _handleExit() {
    return true;
  }
}
